import { ref, reactive } from 'vue';
import ApiService from '@/core/services/api.service';
import { makeToastFaile } from '@/utils/common';
import { removeAccents } from '@/utils/common';
import { cloneDeep, map, find, assign } from 'lodash';
import { PRODUCT_STOCK_QUANTITY_DISPLAY_OPTIONS } from '@/view/pages/stocks/productStockQuantityDisplay';

/**
 * Có tồn + popover "Hiển thị" (settingTable.quantity):
 * - Bỏ "Có tồn" → không gửi checkStock
 * - Chỉ tick 1 loại trong Hiển thị → checkStock = đúng loại đó (vd. chỉ Đang lỗi → errorQuantity)
 * - Tick nhiều loại hoặc không còn tick → checkStock = true (tổng hợp theo API)
 */
function resolveCheckStockFromDisplay(filters, displayQuantityIds) {
  if (filters.haveStock !== true) {
    return undefined;
  }
  const ids = Array.isArray(displayQuantityIds) ? displayQuantityIds : [];
  if (ids.length === 1) {
    const row = PRODUCT_STOCK_QUANTITY_DISPLAY_OPTIONS.find(
      (r) => r.id === ids[0],
    );
    return row ? row.checkStock : true;
  }
  return true;
}

/**
 * Query GET /api/v1/product-stocks theo contract BE:
 * - checkStock: true | inStockQuantity | errorQuantity | …; không gửi khi haveStock = false
 * - searchStore: chuỗi "1,2" hoặc không gửi
 * - searchProduct: trim, bỏ qua nếu rỗng
 * - sortBy: productName | sellingPrice | totalQuantityInStock | <storeId> (cột kho động)
 * - sortOrder: asc | desc
 */
function buildProductStocksListQuery(filters, displayQuantityIds) {
  const params = {};

  const sp =
    filters.searchProduct != null ? String(filters.searchProduct).trim() : '';
  if (sp) {
    params.searchProduct = sp;
  }

  let listId = [];
  if (filters.value && filters.value.length) {
    listId = filters.value
      .map(({ code }) => code)
      .filter((c) => c != null && String(c).trim() !== '');
  }
  if (listId.length) {
    params.searchStore = listId.join(',');
  }

  if (
    filters.selectedProductType != null &&
    filters.selectedProductType !== ''
  ) {
    params.productType = filters.selectedProductType;
  }

  const checkStock = resolveCheckStockFromDisplay(filters, displayQuantityIds);
  if (checkStock !== undefined) {
    params.checkStock = checkStock;
  }

  let listCateId = null;
  if (filters.selectedCateId) {
    listCateId = Array.isArray(filters.selectedCateId)
      ? filters.selectedCateId
      : [filters.selectedCateId];
  }
  if (listCateId && listCateId.length) {
    params.cateId = listCateId;
  }

  let listBrandId = [];
  if (filters.selectedBrands && filters.selectedBrands.length) {
    listBrandId = filters.selectedBrands.map(({ code }) => code);
  }
  if (listBrandId.length) {
    params.brandIds = listBrandId;
  }

  if (filters.selectedCate2Id != null && filters.selectedCate2Id !== '') {
    params.internalCateId = filters.selectedCate2Id;
  }

  if (filters.byParent === true) {
    params.byParent = true;
  }

  if (filters.selectedCompany != null && filters.selectedCompany !== '') {
    params.companyId = filters.selectedCompany;
  }

  if (filters.sortBy) {
    params.sortBy = String(filters.sortBy);
    params.sortOrder = filters.sortOrder || 'asc';
  }

  return params;
}

export const PRODUCT_STOCK_PAGE_SIZE_OPTIONS = [30, 50, 100];

export default function useProductStockList() {
  const listData = ref([]);
  const grandTotals = ref(null);
  const totalItem = ref(0);
  const numberOfPage = ref(0);
  const isLoading = ref(false);
  const fields = ref([]);

  const filters = reactive({
    page: 1,
    limit: 30,
    searchProduct: '',
    selectedStore: null,
    selectedProductType: null,
    /** Đồng bộ với checkbox "Có tồn" trên màn hình */
    haveStock: true,
    selectedCateId: null,
    selectedCateName: '',
    selectedBrandId: null,
    selectedBrandName: '',
    selectedCate2Id: null,
    byParent: false,
    value: [],
    selectedBrands: [],
    selectedCompany: null,
    sortBy: null,
    sortOrder: 'asc',
  });

  const settingTable = reactive({
    quantity: [1, 2, 3, 4, 5, 6, 7],
  });

  const fetchData = async () => {
    isLoading.value = true;

    const params = {
      page: filters.page,
      limit: filters.limit,
      ...buildProductStocksListQuery(filters, settingTable.quantity),
    };

    try {
      const { data } = await ApiService.query('v1/product-stocks', {
        params,
      });
      listData.value = data.data.data;
      numberOfPage.value = data.data.total_page;
      totalItem.value = data.data.total_row;
      grandTotals.value = data.data.totals || null;
    } catch (error) {
      if (error.response) {
        makeToastFaile(
          error.response.data ? error.response.data.message : 'Lỗi',
        );
      } else {
        makeToastFaile(error.message || 'Lỗi');
      }
    } finally {
      isLoading.value = false;
    }
  };

  const getParamFilters = () => {
    const storeIds = filters.value.map(({ code }) => code);
    const listBrandId = filters.selectedBrands.map(({ code }) => code);

    // Handle cateId - can be array or null
    let cateId = null;
    if (filters.selectedCateId) {
      cateId = Array.isArray(filters.selectedCateId)
        ? filters.selectedCateId
        : [filters.selectedCateId];
    }

    const searchProduct = filters.searchProduct
      ? String(filters.searchProduct).trim()
      : '';

    const listQuery = buildProductStocksListQuery(filters, settingTable.quantity);

    return {
      storeIds,
      searchProduct: searchProduct || null,
      cateId: cateId,
      internalCateId: filters.selectedCate2Id || null,
      brandIds: listBrandId,
      productType: filters.selectedProductType || null,
      byParent: filters.byParent || null,
      companyId: filters.selectedCompany || null,
      ...(listQuery.searchStore ? { searchStore: listQuery.searchStore } : {}),
      ...(listQuery.checkStock !== undefined && listQuery.checkStock !== ''
        ? { checkStock: listQuery.checkStock }
        : {}),
      ...(listQuery.sortBy
        ? {
            sortBy: listQuery.sortBy,
            sortOrder: listQuery.sortOrder || 'asc',
          }
        : {}),
    };
  };

  const initializeFields = () => {
    return [
      {
        key: 'productName',
        label: 'Tên sản phẩm',
        sortable: true,
        thStyle: {
          textAlign: 'center',
          color: 'rgb(24, 28, 50)',
          width: '400px',
          minWidth: '400px',
          maxWidth: '400px',
        },
        tdStyle: {
          width: '400px',
          minWidth: '400px',
          maxWidth: '400px',
        },
      },
      {
        key: 'productCode',
        label: 'Mã sản phẩm',
        sortable: false,
        thStyle: {
          textAlign: 'center',
          color: 'rgb(24, 28, 50)',
          width: '200px',
          minWidth: '200px',
          maxWidth: '200px',
        },
        tdStyle: {
          width: '200px',
          minWidth: '200px',
          maxWidth: '200px',
        },
      },
      {
        key: 'sellingPrice',
        label: 'Giá bán',
        sortable: true,
        thStyle: {
          textAlign: 'center',
          color: 'rgb(24, 28, 50)',
          width: '120px',
          minWidth: '120px',
          maxWidth: '120px',
        },
        tdStyle: {
          width: '160px',
          minWidth: '160px',
          maxWidth: '160px',
        },
      },
      {
        key: 'originalPrice',
        label: 'Giá nhập',
        sortable: false,
        thStyle: {
          textAlign: 'center',
          color: 'rgb(24, 28, 50)',
          width: '120px',
          minWidth: '120px',
          maxWidth: '120px',
        },
        tdStyle: {
          width: '120px',
          minWidth: '120px',
          maxWidth: '120px',
        },
      },
      {
        key: 'totalQuantityInStock',
        label: 'Tổng tồn',
        sortable: true,
        thStyle: {
          textAlign: 'center',
          color: 'rgb(24, 28, 50)',
          width: '100px',
          minWidth: '100px',
          maxWidth: '100px',
        },
        tdStyle: {
          width: '100px',
          minWidth: '100px',
          maxWidth: '100px',
        },
      },
    ];
  };

  const fetchStoreFields = async () => {
    fields.value = initializeFields();

    let listId = [];
    if (filters.value.length) {
      listId = filters.value.map(({ code }) => code);
    }

    const param = {
      listId: listId,
    };

    try {
      const response = await ApiService.query('/stores/getStores', {
        params: param,
      });
      const stores = response.data.data;

      stores.forEach((element) => {
        const field = {
          key: element.id + '',
          label: element.shortName,
          sortable: true,
          thStyle: {
            textAlign: 'center',
            color: 'rgb(24, 28, 50)',
            width: '100px',
            minWidth: '100px',
            maxWidth: '100px',
          },
          tdStyle: {
            width: '100px',
            minWidth: '100px',
            maxWidth: '100px',
          },
          tdClass: 'text-right',
          formatter: (value) => {
            const haveShowBase = settingTable.quantity.includes(1);
            let strBase = '';
            if (value.quantityInStock) {
              strBase = `<span class='mr-1 ${
                !haveShowBase ? 'd-none' : ''
              }' title='Tồn có thể bán'><i class='fas fa-check-circle fa-xs icon instock-color'></i><b class='text-dark'>${
                value.quantityInStock
              }</b></span>`;
            }

            if (value.transferInStock) {
              const haveShow = settingTable.quantity.includes(2);
              strBase += `<span class='prop-transfer mr-1 ${
                !haveShow ? 'd-none' : ''
              }' title='Đang chuyển kho'><i class='fas fa-exchange-alt fa-xs icon text-primary'></i><b class='text-primary font-size-setting'>+${value.transferInStock}</b></span>`;
            }

            if (value.holdingInStock) {
              const haveShow = settingTable.quantity.includes(3);
              strBase += `<span class='prop-holding mr-1 ${
                !haveShow ? 'd-none' : ''
              }' title='Đang giữ hàng'><i class='fas fa-hand-paper fa-xs icon holding-color'></i><b class='holding-color font-size-setting'>+${value.holdingInStock}</b></span>`;
            }

            if (value.deliveryInStock) {
              const haveShow = settingTable.quantity.includes(4);
              strBase += `<span class='prop-holding mr-1 ${
                !haveShow ? 'd-none' : ''
              }' title='Đang vận chuyển'><i class='fas fa-truck fa-xs icon delivery-color'></i><b class='delivery-color font-size-setting'>+${value.deliveryInStock}</b></span>`;
            }

            if (value.warrantyInStock) {
              const haveShow = settingTable.quantity.includes(5);
              strBase += `<span class='prop-holding mr-1 ${
                !haveShow ? 'd-none' : ''
              }' title='Đang bảo hành'><i class='fas fa-wrench fa-xs icon warranty-color'></i><b class='warranty-color font-size-setting'>+${value.warrantyInStock}</b></span>`;
            }
            if (value.errorInStock) {
              const haveShow = settingTable.quantity.includes(6);
              strBase += `<span class='mr-1 ${
                !haveShow ? 'd-none' : ''
              }' title='Đang lỗi'><i class='fas fa-exclamation-circle fa-xs icon error-color'></i><b class='error-color font-size-setting'>+${value.errorInStock}</b></span>`;
            }
            if (value.lendingInStock) {
              const haveShow = settingTable.quantity.includes(7);
              strBase += `<span class='mr-1 ${
                !haveShow ? 'd-none' : ''
              }' title='Đang cho mượn'><i class='fas fa-handshake fa-xs icon lending-color'></i><b class='lending-color font-size-setting'>+${value.lendingInStock}</b></span>`;
            }

            return strBase;
          },
        };

        fields.value.push(field);
      });

      return fields.value;
    } catch (error) {
      makeToastFaile(error.message || 'Lỗi khi tải dữ liệu cửa hàng');
      return fields.value;
    }
  };

  const filterOptions = (options, textInput, prop, limit) => {
    return cloneDeep(options)
      .filter((item) => {
        if (item) {
          const nameWTUnicode = removeAccents(item[prop] || '');
          const nameInputWTUnicode = removeAccents(textInput);
          const index = nameWTUnicode
            .toLowerCase()
            .indexOf(nameInputWTUnicode.toLowerCase());

          if (index > -1) {
            return true;
          }
        }
        return false;
      })
      .slice(0, limit);
  };

  const searchStore = (options, filteredOptions, textInput = '') => {
    let optionsList = cloneDeep(options);

    if (!textInput || !textInput.trim().length) {
      return map(optionsList, (obj) => {
        return assign(obj, find(filteredOptions, { id: obj.id }));
      });
    }

    const indexChooseAll = optionsList.findIndex((prop) => prop.id === -1);

    if (indexChooseAll > -1) {
      optionsList.splice(indexChooseAll, 1);
    }

    optionsList = map(optionsList, (obj) => {
      return assign(obj, find(filteredOptions, { id: obj.id }));
    });

    return filterOptions(optionsList, textInput, 'name', 10);
  };

  return {
    listData,
    grandTotals,
    totalItem,
    numberOfPage,
    isLoading,
    fields,
    filters,
    settingTable,
    fetchData,
    getParamFilters,
    initializeFields,
    fetchStoreFields,
    filterOptions,
    searchStore,
  };
}
